import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { ExternalLink, Copy, Check, RefreshCw, Loader2, Pencil, RotateCcw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import type { LibraryItem, VeevaSubtype } from '@/data/mock'

interface DocumentDetailsPanelProps {
  item: LibraryItem
  onReprocess?: (sourceId: string) => void
  onUpdateCitation?: (
    sourceId: string,
    changes: { amaOverride?: string | null; shortOverride?: string | null }
  ) => void
}

type AccordionState = 'open' | 'closed' | 'hidden'

interface SubtypeSections {
  authors: boolean
  abstract: boolean
  source: AccordionState
  topics: AccordionState
  product: AccordionState
}

const SUBTYPE_SECTIONS: Record<VeevaSubtype, SubtypeSections> = {
  'Publication':                  { authors: true,  abstract: true,  source: 'open',   topics: 'closed', product: 'hidden' },
  'Data on File':                 { authors: false, abstract: true,  source: 'closed', topics: 'hidden', product: 'open'   },
  'Labeling':                     { authors: false, abstract: false, source: 'closed', topics: 'closed', product: 'open'   },
  'Government/Authority Report':  { authors: false, abstract: true,  source: 'open',   topics: 'hidden', product: 'hidden' },
  'Website':                      { authors: false, abstract: false, source: 'open',   topics: 'hidden', product: 'hidden' },
  'Monograph':                    { authors: false, abstract: true,  source: 'closed', topics: 'open',   product: 'open'   },
  'Instructions for Use':         { authors: false, abstract: false, source: 'closed', topics: 'hidden', product: 'open'   },
  'Important Safety Information': { authors: false, abstract: false, source: 'closed', topics: 'hidden', product: 'open'   },
}

function MetaField({ label, value }: { label: string; value?: string }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="text-xs text-foreground mt-0.5 break-words">{value}</dd>
    </div>
  )
}

/** Derive a human-readable label for how the metadata was sourced.
 *  Reads enrichment_telemetry_summary.path_taken — see backend
 *  EnrichmentTrace._compute_summary for the canonical step names. */
function deriveMetadataSourceBadge(item: LibraryItem):
  | { label: string; tooltip: string; tone: 'authoritative' | 'derived' | 'ai' }
  | null {
  const path = item.enrichmentTelemetrySummary?.path_taken
  if (!Array.isArray(path) || path.length === 0) return null
  if (path.includes('pubmed_lookup') && !path.includes('llm_fallback_extraction')) {
    return { label: 'PubMed', tooltip: 'Metadata fetched from PubMed via DOI.', tone: 'authoritative' }
  }
  if (path.includes('pubmed_pmid_fastpath')) {
    return {
      label: 'PubMed',
      tooltip: 'Metadata fetched from PubMed via PMID printed in the document.',
      tone: 'authoritative',
    }
  }
  if (path.includes('pubmed_xml_promote')) {
    return {
      label: 'PubMed (via search)',
      tooltip: 'AI-extracted citation matched a PubMed record; canonical fields applied.',
      tone: 'authoritative',
    }
  }
  if (path.includes('crossref_lookup')) {
    return { label: 'Crossref', tooltip: 'Metadata fetched from Crossref via DOI.', tone: 'derived' }
  }
  if (path.includes('llm_fallback_extraction')) {
    return { label: 'AI-extracted', tooltip: 'No DOI or PubMed match — fields extracted by AI.', tone: 'ai' }
  }
  return null
}

function CitationCopyRow({
  label,
  text,
  isOverridden = false,
  onRestore,
}: {
  label: string
  text: string
  isOverridden?: boolean
  onRestore?: () => void
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success('Copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-muted-foreground font-medium">{label}</span>
        {isOverridden && (
          <>
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-[8px] bg-muted text-muted-foreground uppercase tracking-wide">
              Edited
            </span>
            {onRestore && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 -m-0.5 text-muted-foreground hover:text-foreground"
                    onClick={onRestore}
                  >
                    <RotateCcw className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Restore AI-generated citation</TooltipContent>
              </Tooltip>
            )}
          </>
        )}
      </div>
      <div className="flex items-start gap-2">
        <div className="flex-1 bg-muted/50 p-2 rounded text-xs font-mono text-muted-foreground break-words leading-relaxed">
          <ReactMarkdown components={{ p: ({ children }) => <>{children}</>, em: ({ children }) => <i>{children}</i> }}>
            {text}
          </ReactMarkdown>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 mt-0.5 text-muted-foreground hover:text-foreground"
          onClick={handleCopy}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" strokeWidth={1.5} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />}
        </Button>
      </div>
    </div>
  )
}

function CitationEditField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (next: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-muted-foreground font-medium">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-background border border-border rounded p-2 text-xs font-mono text-foreground leading-relaxed min-h-[72px] resize-y focus:outline-none focus:ring-1 focus:ring-ring"
      />
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
      {children}
    </h3>
  )
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-card border border-border/40 rounded-lg ${className}`}>
      {children}
    </div>
  )
}

function AccordionCard({
  value,
  title,
  defaultOpen,
  children,
}: {
  value: string
  title: string
  defaultOpen: boolean
  children: React.ReactNode
}) {
  return (
    <Card className="overflow-hidden">
      <Accordion type="single" collapsible defaultValue={defaultOpen ? value : undefined}>
        <AccordionItem value={value} className="border-b-0">
          <AccordionTrigger className="px-3 py-2.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wide hover:no-underline">
            {title}
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3">
            {children}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  )
}

export function DocumentDetailsPanel({ item, onReprocess, onUpdateCitation }: DocumentDetailsPanelProps) {
  const [showFullAbstract, setShowFullAbstract] = useState(false)
  const [isEditingCitation, setIsEditingCitation] = useState(false)
  const [draftAma, setDraftAma] = useState('')
  const [draftShort, setDraftShort] = useState('')
  const sections = SUBTYPE_SECTIONS[item.fileSubtype]
  const isPublication = item.fileSubtype === 'Publication'

  const displayedAma = item.amaCitationOverride ?? item.amaCitation
  const displayedShort = item.shortCitationOverride ?? item.shortCitation

  function startEditingCitation() {
    setDraftAma(displayedAma)
    setDraftShort(displayedShort ?? '')
    setIsEditingCitation(true)
  }

  function cancelEditingCitation() {
    setIsEditingCitation(false)
  }

  function saveEditingCitation() {
    const changes: { amaOverride?: string | null; shortOverride?: string | null } = {}

    const trimmedAma = draftAma.trim()
    if (trimmedAma !== item.amaCitation) {
      changes.amaOverride = trimmedAma
    } else if (item.amaCitationOverride !== undefined) {
      // User typed back the original AI value — clear the override.
      changes.amaOverride = null
    }

    if (isPublication && item.shortCitation !== undefined) {
      const trimmedShort = draftShort.trim()
      if (trimmedShort !== (item.shortCitation ?? '')) {
        changes.shortOverride = trimmedShort.length > 0 ? trimmedShort : null
      } else if (item.shortCitationOverride !== undefined) {
        changes.shortOverride = null
      }
    }

    if (Object.keys(changes).length > 0) {
      onUpdateCitation?.(item.id, changes)
    }
    setIsEditingCitation(false)
  }

  const hasSourceData = !!(item.language || item.volume || item.pageRange || item.source || item.sourceDate || item.doi || item.url)
  const hasTopicsData = !!((item.activeIngredients?.length) || (item.meshTerms?.length))
  const hasProductData = !!(item.therapeuticArea || item.productName || item.companyName || item.brand)

  const showSource = sections.source !== 'hidden' && hasSourceData
  const showTopics = sections.topics !== 'hidden' && hasTopicsData
  const showProduct = sections.product !== 'hidden' && hasProductData
  const showAuthors = sections.authors && ((item.authors?.length ?? 0) > 0 || item.status === 'processing')
  const showAbstract = sections.abstract && (item.abstract || item.status === 'processing')

  const ragBadge = item.isActiveInRag
    ? { label: 'Active', className: 'bg-[var(--status-verified-bg)] text-[var(--status-verified-text)]' }
    : { label: 'Excluded', className: 'bg-muted text-muted-foreground' }

  const metadataSourceBadge = deriveMetadataSourceBadge(item)
  const metadataSourceClass: Record<NonNullable<ReturnType<typeof deriveMetadataSourceBadge>>['tone'], string> = {
    authoritative: 'bg-[var(--status-verified-bg)] text-[var(--status-verified-text)]',
    derived: 'bg-[var(--status-partial-bg)] text-[var(--status-partial-text)]',
    ai: 'bg-muted text-muted-foreground',
  }

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-2.5 p-3 bg-muted/40 min-h-full">
        {/* Header */}
        <Card className="p-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h2 className="text-sm font-semibold text-foreground leading-snug">
              <ReactMarkdown components={{ p: ({ children }) => <>{children}</>, em: ({ children }) => <i>{children}</i> }}>
                {item.title}
              </ReactMarkdown>
            </h2>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                  disabled={item.status === 'processing'}
                  onClick={() => onReprocess?.(item.id)}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${item.status === 'processing' ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Re-run AI metadata extraction</TooltipContent>
            </Tooltip>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-[10px] bg-muted text-muted-foreground">
              {item.fileSubtype}
            </span>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-[10px] ${ragBadge.className}`}>
              {ragBadge.label}
            </span>
            {metadataSourceBadge && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-[10px] cursor-help ${metadataSourceClass[metadataSourceBadge.tone]}`}>
                    {metadataSourceBadge.label}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{metadataSourceBadge.tooltip}</TooltipContent>
              </Tooltip>
            )}
            {item.status === 'processing' && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-[10px] bg-muted text-muted-foreground">
                <Loader2 className="h-2.5 w-2.5 animate-spin" strokeWidth={1.5} />
                Refining metadata
              </span>
            )}
            {item.status === 'failed' && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-[10px] bg-[var(--status-error-bg)] text-[var(--status-error-text)]">
                Failed
              </span>
            )}
          </div>
        </Card>

        {/* Citation */}
        <Card className="p-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <SectionHeader>Citation</SectionHeader>
              {!isEditingCitation && item.status !== 'processing' && onUpdateCitation && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 -m-1 text-muted-foreground hover:text-foreground"
                      onClick={startEditingCitation}
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit citation</TooltipContent>
                </Tooltip>
              )}
              {isEditingCitation && (
                <div className="flex items-center gap-1 -m-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={cancelEditingCitation}
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Cancel</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-foreground"
                        onClick={saveEditingCitation}
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Save</TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>

            {item.status === 'processing' && !item.shortCitation && !item.amaCitation ? (
              <div className="flex flex-col gap-2">
                <div className="h-3 w-20 animate-pulse bg-muted rounded" />
                <div className="h-12 w-full animate-pulse bg-muted rounded" />
              </div>
            ) : isEditingCitation ? (
              <>
                {isPublication && (
                  <CitationEditField
                    label="Short AMA"
                    value={draftShort}
                    onChange={setDraftShort}
                  />
                )}
                <CitationEditField
                  label={isPublication ? 'AMA' : 'Citation'}
                  value={draftAma}
                  onChange={setDraftAma}
                />
              </>
            ) : isPublication ? (
              <>
                {displayedShort && (
                  <CitationCopyRow
                    label="Short AMA"
                    text={displayedShort}
                    isOverridden={item.shortCitationOverride !== undefined}
                    onRestore={onUpdateCitation ? () => onUpdateCitation(item.id, { shortOverride: null }) : undefined}
                  />
                )}
                <CitationCopyRow
                  label="AMA"
                  text={displayedAma}
                  isOverridden={item.amaCitationOverride !== undefined}
                  onRestore={onUpdateCitation ? () => onUpdateCitation(item.id, { amaOverride: null }) : undefined}
                />
              </>
            ) : (
              <CitationCopyRow
                label="Citation"
                text={displayedAma}
                isOverridden={item.amaCitationOverride !== undefined}
                onRestore={onUpdateCitation ? () => onUpdateCitation(item.id, { amaOverride: null }) : undefined}
              />
            )}
          </div>
        </Card>

        {/* Authors (Publications only) */}
        {showAuthors && (
          <Card className="p-3">
            <div className="flex flex-col gap-1.5">
              <SectionHeader>Authors</SectionHeader>
              {item.authors && item.authors.length > 0 ? (
                <p className="text-xs text-foreground">{item.authors.join(', ')}</p>
              ) : (
                <div className="h-3 w-40 animate-pulse bg-muted rounded" />
              )}
            </div>
          </Card>
        )}

        {/* Abstract */}
        {showAbstract && (
          <Card className="p-3">
            <div className="flex flex-col gap-1.5">
              <SectionHeader>Abstract</SectionHeader>
              {item.abstract ? (
                <>
                  <p className={`text-xs text-foreground leading-relaxed ${!showFullAbstract ? 'line-clamp-3' : ''}`}>
                    {item.abstract}
                  </p>
                  {item.abstract.length > 200 && (
                    <button
                      onClick={() => setShowFullAbstract((v) => !v)}
                      className="text-[11px] text-pharma-primary hover:underline self-start"
                    >
                      {showFullAbstract ? 'Show less' : 'Read more'}
                    </button>
                  )}
                </>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <div className="h-3 w-full animate-pulse bg-muted rounded" />
                  <div className="h-3 w-full animate-pulse bg-muted rounded" />
                  <div className="h-3 w-3/4 animate-pulse bg-muted rounded" />
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Source */}
        {showSource && (
          <AccordionCard value="source" title="Source" defaultOpen={sections.source === 'open'}>
            <dl className="grid grid-cols-3 gap-x-3 gap-y-2.5">
              <MetaField label="Publication" value={item.source} />
              <MetaField label="Date" value={item.sourceDate} />
              <MetaField label="Volume" value={item.volume} />
              <MetaField label="Pages" value={item.pageRange} />
              <MetaField label="Language" value={item.language} />
              {(item.doi || item.url) && (
                <div>
                  <dt className="text-[11px] text-muted-foreground">Links</dt>
                  <dd className="flex items-center gap-2 mt-0.5">
                    {item.doi && (
                      <a
                        href={`https://doi.org/${item.doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-pharma-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                        DOI
                      </a>
                    )}
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-pharma-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                        URL
                      </a>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </AccordionCard>
        )}

        {/* Topics */}
        {showTopics && (
          <AccordionCard value="topics" title="Topics" defaultOpen={sections.topics === 'open'}>
            <div className="flex flex-col gap-3">
              {item.activeIngredients && item.activeIngredients.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] text-muted-foreground">Active Ingredients</span>
                  <div className="flex flex-wrap gap-1.5">
                    {item.activeIngredients.map((ing) => (
                      <span
                        key={ing}
                        className="text-[10px] font-medium px-2 py-0.5 rounded-[10px] bg-muted text-muted-foreground"
                      >
                        {ing}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {item.meshTerms && item.meshTerms.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-muted-foreground">MeSH Terms</span>
                    {item.meshSource && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-[10px] text-muted-foreground italic cursor-help">
                            ({item.meshSource === 'pubmed' ? 'PubMed' : 'auto-suggested'})
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {item.meshSource === 'pubmed'
                            ? 'Curated by NLM indexers (authoritative).'
                            : 'Auto-suggested via MeSH on Demand from the abstract.'}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {item.meshTerms.map((term) => (
                      <span
                        key={term}
                        className="text-[10px] font-medium px-2 py-0.5 rounded-[10px] border border-border text-muted-foreground"
                      >
                        {term}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </AccordionCard>
        )}

        {/* Product */}
        {showProduct && (
          <AccordionCard value="product" title="Product" defaultOpen={sections.product === 'open'}>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5">
              <MetaField label="Therapeutic Area" value={item.therapeuticArea} />
              <MetaField label="Product" value={item.productName} />
              <MetaField label="Brand" value={item.brand} />
              <MetaField label="Company" value={item.companyName} />
            </dl>
          </AccordionCard>
        )}
      </div>
    </ScrollArea>
  )
}
